import { useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { CliniciansHelper } from "@/Common/Helpers/CliniciansHelper";
import { PopulationBasedFundingHelper } from "@/Common/Helpers/PopulationBasedFundingHelper";
import { PopulationBasedFundingFormData, PopulationBasedFundingFormEnum } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { PopulationBasedFundingEventDeregisteredReason } from "@/Common/Models/UI/Enums";
import { ClickableTextPanelContainer } from "../ClickableText/ClickableTextPanelContainer";
import { CheckboxControl } from "../Inputs/CheckboxControl";
import { DateSelector } from "../Inputs/DateSelector";
import { SelectLookupControl } from "../Inputs/SelectLookupControl";
import { TextAreaResizable } from "../Inputs/TextAreaResizable";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "../Search/PersonSearchAutocompleteCustom";

interface IPopulationBasedFundingBasePopupBodyProps {
    formData: PopulationBasedFundingFormData;
    onChange: (formData: PopulationBasedFundingFormData) => void;
    formType: PopulationBasedFundingFormEnum;
    latestRegistrationEventEffectiveDate?: Date;
}

function PopulationBasedFundingBasePopupBody(props: IPopulationBasedFundingBasePopupBodyProps): JSX.Element {
    const { getText } = useTranslation();
    const clinicianSearchStrategy: ClinicianSearchStrategy = CliniciansHelper.GetAllCliniciansSearchStrategy();
    let selectedItemList = props.formData.SelectedPhysician ? [props.formData.SelectedPhysician] : null;
    const [isNoteClickableTextShown, toggleClickableNotesVisibility] = useState(false);
    const [isCommentClickableTextShown, toggleClickableCommentVisibility] = useState(false);

    const reasons = PopulationBasedFundingHelper.getDeregistrationReasonsDropdownItems();
    const administrativeCodes = PopulationBasedFundingHelper.getAdministrativeCodesDropdownItems();

    const deregistrationClass = `${props.formData.SelectedDeregisterReason ? "" : "invalid"} deregistration-field form-control myle-control`;

    const dateLabel = (props.formType === PopulationBasedFundingFormEnum.Register) ? getText("PopulationBasedFunding_RegistrationDateLabel") : getText("PopulationBasedFunding_DeregistrationDateLabel");

    const hasHistoricalNotes = props.formData.NotesHistory?.any();

    function renderRow(label: string | JSX.Element, element: JSX.Element, col: number = 10): JSX.Element {
        return (
            <div className="row margin-md-top populationBasedFunding-popup-row">
                <div className={"col-md-" + col}>
                    <label className="bold">{label}</label>
                    {element}
                </div>
            </div>
        );
    }

    const lastRegistrationDate = renderRow(
        getText("PopulationBasedFunding_RegistrationDateLabel"),
        <DateSelector
            id="locked_registration_dateselector_input"
            cssClass="dateselector_input"
            dateFormat="yyyy-MM-dd"
            date={props.latestRegistrationEventEffectiveDate}
            disabled
        />,
        12
    );

    const renderDeregistrationRows = (): JSX.Element => (
        <>
            {renderRow(
                getText("ReasonLabel"),
                <>
                    <select
                        value={props.formData.SelectedDeregisterReason?.toString() ?? ""}
                        className={deregistrationClass}
                        onChange={p => props.onChange({ ...props.formData, SelectedDeregisterReason: Number(p.target.value) })}
                    >
                        <option hidden> </option>
                        {reasons.map(p => <option key={"key_" + p.Code} value={p.Code}>{p.DisplayName}</option>)}
                    </select>
                </>
            )}
            {props.formData.SelectedDeregisterReason == PopulationBasedFundingEventDeregisteredReason.AnotherReasonIndicatedInNotes
             && renderRow(
                 getText("CommentLabel"),
                 <>
                     <TextAreaResizable
                         id="Custome_Deregistration_note_input"
                         className="pop_up_text_area"
                         verticalResizeOnly={true}
                         isDisabled={false}
                         invalid={!props.formData.DeregistrationOrProposalCustomReason}
                         text={props.formData.DeregistrationOrProposalCustomReason}
                         onChange={(value: string) => props.onChange({ ...props.formData, DeregistrationOrProposalCustomReason: value })}
                     />
                     {isCommentClickableTextShown && (
                         <ClickableTextPanelContainer
                             cssClass="text-helper-container"
                             context="PBFNOTES"
                             inputElementId="Custome_Deregistration_note_input"
                             showOverlay={true}
                             onClose={() => toggleClickableCommentVisibility(false)}
                         />
                     )}
                     <div className="text-right select_list_section">
                         <a onClick={() => toggleClickableCommentVisibility(true)}> {cacheStore.GetString("SelectFromListLabel")} </a>
                     </div>
                 </>
             )
            }
        </>
    );

    return (
        <div className="populationBasedFunding-content">
            <div className="padding-md">
                {renderRow(
                    getText("PhysicianNameLabel"),
                    <PersonSearchAutocompleteCustom
                        componentId="physician_list_search_input"
                        disabled={props.formType === PopulationBasedFundingFormEnum.Deregister}
                        focusOnLoad={false}
                        allowMultipleItemSelection={false}
                        closeDropDownAfterSelection
                        selectedItemList={selectedItemList?.toImmutable()}
                        valid={!!props.formData.SelectedPhysician}
                        searchStrategy={clinicianSearchStrategy}
                        onSelectionCallback={item => props.onChange({ ...props.formData, SelectedPhysician: item.Item })}
                        onClearCallback={() => props.onChange({ ...props.formData, SelectedPhysician: null })}
                    />
                )}
                {props.formType === PopulationBasedFundingFormEnum.Deregister && lastRegistrationDate}
                {renderRow(
                    dateLabel,
                    <DateSelector
                        id="registration_dateselector_input"
                        cssClass="dateselector_input"
                        dateFormat="yyyy-MM-dd"
                        toDayEnabled
                        date={props.formData.SelectedEffectiveDate}
                        valid={!!props.formData.SelectedEffectiveDate}
                        onChangeCallback={date => props.onChange({ ...props.formData, SelectedEffectiveDate: date })}
                    />,
                    12
                )}
                {props.formType === PopulationBasedFundingFormEnum.Register && (
                    renderRow(
                        getText("AdministrativeCodeLabel"),
                        <SelectLookupControl
                            className="administrative-code-field"
                            IncludeEmptyChoice={false}
                            Value={props.formData.AdministrativeCode.toString()}
                            AvailableValues={administrativeCodes}
                            Callback={value => props.onChange({ ...props.formData, AdministrativeCode: value })}
                        />,
                        12
                    )
                )}
                {props.formType === PopulationBasedFundingFormEnum.Deregister &&
                    renderDeregistrationRows()
                }
                {renderRow(
                    getText("NotesLabel"),
                    <>
                        <TextAreaResizable
                            id="registration_note_input"
                            className="pop_up_text_area"
                            verticalResizeOnly={true}
                            isDisabled={false}
                            text={props.formData.Notes}
                            onChange={(value: string) => props.onChange({ ...props.formData, Notes: value })}
                            maxLength={1000}
                        />
                        {isNoteClickableTextShown && (
                            <ClickableTextPanelContainer
                                cssClass="text-helper-container"
                                context="PBFNOTES"
                                inputElementId="registration_note_input"
                                onClose={() => toggleClickableNotesVisibility(false)}
                            />
                        )}
                        <div className="text-right select_list_section">
                            <a onClick={() => toggleClickableNotesVisibility(true)}> {cacheStore.GetString("SelectFromListLabel")} </a>
                        </div>
                    </>
                )}
                {hasHistoricalNotes &&
                    renderRow(
                        getText("NotesHistoryLabel"),
                        <TextAreaResizable
                            id="registration_note_history_input"
                            className="pop_up_text_area"
                            verticalResizeOnly={true}
                            isDisabled={true}
                            minHeightPx={100}
                            text={props.formData.NotesHistory.join("\r\n")}
                            maxLength={1000}
                        />,
                        12
                    )
                }
                {props.formType === PopulationBasedFundingFormEnum.Deregister && (
                    <div className="row margin-md-top populationBasedFunding-popup-row">
                        <div className="col-md-12">
                            <CheckboxControl
                                id="dereigstration_will_continue_treatment_chekbox"
                                checked={props.formData.WillContinueToSeePhysician}
                                className="populaionBasedFunding_CheckBox"
                                onChange={state => props.onChange({ ...props.formData, WillContinueToSeePhysician: state })}
                            />
                            <label htmlFor="dereigstration_will_continue_treatment_chekbox">{getText("PopulationBasedFunding_WillContinueTreatmentLabel")}</label>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export { PopulationBasedFundingBasePopupBody };
